// src/pages/CourseTime/hooks/useActivityWidget.js
import { useState, useCallback } from 'react';
import { fetchActivityResponsesService, fetchUserActivitiesService, } from '../../../services/activites.service';
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

export const useActivityWidget = (identifier, profile, pageLc) => {
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userActivities, setUserActivities] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activityResponses, setActivityResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [responsesLoading, setResponsesLoading] = useState({});
  const [error, setError] = useState(null);

  // Helper function to get profile from localStorage if not available
  const getProfileFromStorage = () => {
    try {
      const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
      if (org_code) {
        const available_profiles = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES));
        if (available_profiles) {
          return available_profiles.find(profile => profile.org_code === org_code);
        }
      }
    } catch (error) {
      console.error('Error loading profile from localStorage:', error);
    }
    return null;
  };

  // Format activity type for display
  const formatActivityType = (activityType) => {
    const lcKeyMap = {
      JOURNALING_SELF_REFLECTION_ACTIVITY: 'ct__activity_type_journaling',
      SHARE_WORK_FILE_ACTIVITY: 'ct__activity_type_share_work',
      POLL_QUIZ_ACTIVITY: 'ct__activity_type_poll',
      DISCUSSION_ACTIVITY: 'ct__activity_type_discussion'
    };

    const lcKey = lcKeyMap[activityType];
    if (lcKey) return GET_APP_LOCALIZE_TEXT(pageLc, lcKey);
    return activityType;
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));

    if (diffInMinutes < 1) return GET_APP_LOCALIZE_TEXT(pageLc, 'ct__just_now');
    if (diffInMinutes < 60) return `${diffInMinutes}${GET_APP_LOCALIZE_TEXT(pageLc, 'ct__minutes_ago_suffix')}`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}${GET_APP_LOCALIZE_TEXT(pageLc, 'ct__hours_ago_suffix')}`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}${GET_APP_LOCALIZE_TEXT(pageLc, 'ct__days_ago_suffix')}`;

    return messageTime.toLocaleDateString();
  };

  // Fetch activity responses for a specific activity
  const fetchActivityResponses = useCallback(async (activity) => {
    if (!identifier || !activity) {
      console.warn('Missing identifier or activity for fetching responses');
      return;
    }

    const responseKey = `${activity.slide_id}_${activity.activity_type}`;

    try {
      setResponsesLoading(prev => ({ ...prev, [responseKey]: true }));
      const response = await fetchActivityResponsesService(
        identifier,
        activity.activity_type,
        activity.segment,
        activity.slide_index,
        activity.slide_id,
        activity.session
      );

      if (response.success && response.data) {
        setActivityResponses(prev => ({
          ...prev,
          [responseKey]: response.data
        }));
      } else {
        console.error('Failed to load activity responses');
      }
    } catch (error) {
      console.error('Error fetching activity responses:', error);
    } finally {
      setResponsesLoading(prev => ({ ...prev, [responseKey]: false }));
    }
  }, [identifier]);

  // Fetch user activities
  const fetchUserActivities = useCallback(async () => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }

    if (!identifier || !currentProfile?._id) {
      console.warn('Missing identifier or profile_id for fetching user activities');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetchUserActivitiesService(identifier, currentProfile._id);

      if (response.success && response.data) {
        const activities = response.data.activities || [];
        setUserActivities(activities);

        // Set first tab as active if activities exist
        if (activities.length > 0) {
          setActiveTabIndex(0);
          // Fetch responses for the first tab after activities are loaded
          fetchActivityResponses(activities[0]);
        }
      } else {
        setError('Failed to load user activities');
      }
    } catch (error) {
      console.error('Error fetching user activities:', error);
      setError('Failed to load user activities');
    } finally {
      setLoading(false);
    }
  }, [identifier, profile, fetchActivityResponses]);

  // Open modal and fetch initial data
  const openModal = useCallback(() => {
    setIsModalOpen(true);
    // Always fetch user activities when modal opens to get latest list of tabs
    fetchUserActivities();
  }, [fetchUserActivities]);

  // Close modal
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((index) => {
    setActiveTabIndex(index);

    // Always fetch responses for the selected activity when tab is clicked/switched
    const activity = userActivities[index];
    if (activity) {
      fetchActivityResponses(activity);
    }
  }, [userActivities, fetchActivityResponses]);

  // Get current activity
  const getCurrentActivity = () => {
    return userActivities[activeTabIndex] || null;
  };

  // Get current activity responses
  const getCurrentActivityResponses = () => {
    const activity = getCurrentActivity();
    if (!activity) return null;

    const responseKey = `${activity.slide_id}_${activity.activity_type}`;
    return activityResponses[responseKey] || null;
  };

  // Check if current activity responses are loading
  const isCurrentActivityLoading = () => {
    const activity = getCurrentActivity();
    if (!activity) return false;

    const responseKey = `${activity.slide_id}_${activity.activity_type}`;
    return responsesLoading[responseKey] || false;
  };

  return {
    // State
    isModalOpen,
    userActivities,
    activeTabIndex,
    loading,
    error,

    // Functions
    openModal,
    closeModal,
    handleTabChange,
    getCurrentActivity,
    getCurrentActivityResponses,
    isCurrentActivityLoading,
    formatActivityType,
    formatTimestamp,

    // Utilities
    fetchUserActivities,
    fetchActivityResponses
  };
};
